import { ArrowUpRight, CrossIcon, PlayIcon } from 'assets/icons';
import { ContentIcons } from 'assets/icons/content-icons';
import { AudioPlayer } from 'common/audio-player';
import { Button, ButtonStyle } from 'common/button';
import { AlbumContentType } from 'common/enums';
import { HtmlRenderer } from 'common/html-renderer';
import { UserInitials } from 'common/user-initials';
import { AppContext } from 'context/app-context';
import { adaptContentType, classnames, getInitials } from 'helpers/utils';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { goToPage, RouteName } from 'routes';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import { Navigation, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './expand-carousel.module.scss';

interface ExpandCarouselProps {
  contents: AlbumContentSerialized[];
  initialIndex?: number;
  onClose: () => void;
}

export const ExpandCarousel: React.FC<ExpandCarouselProps> = ({
  contents,
  initialIndex = 0,
  onClose,
}) => {
  const { state } = useContext(AppContext);
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(initialIndex);
  const swiperPrevId = 'custom-swiper-prev';
  const swiperNextId = 'custom-swiper-next';

  // The whole vault in view mode: a member's Memories view, or the public
  // vault view for a guest recording without an account.
  const viewVault = () => {
    const vaultId = state.currentVault!.id;
    if (state.logged) {
      goToPage(RouteName.MemoriesViewScreen, { id: vaultId });
    } else {
      goToPage(RouteName.VaultViewMode, { vaultId });
    }
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const renderContent = (content: AlbumContentSerialized) => {
    switch (adaptContentType(content)) {
      case AlbumContentType.video:
        return (
          <video
            src={content.mediaUrl}
            controls
            className={styles.mediaContent}
            poster={content.thumbnailUrl}
          >
            <track kind="captions" srcLang="en" label="English captions" />
          </video>
        );
      case AlbumContentType.audio:
        return (
          <div className={styles.audioContainer}>
            <AudioPlayer videoUrl={content.mediaUrl} />
          </div>
        );
      case AlbumContentType.image:
        return (
          <img
            src={content.mediaUrl}
            alt={content.name}
            className={styles.mediaContent}
          />
        );
      case AlbumContentType.document:
        return (
          <div className={styles.documentContent}>
            <iframe
              src={content.mediaUrl}
              className={styles.mediaContent}
              title={content.name}
            />
          </div>
        );
      case AlbumContentType.write:
        // TODO: Unify the logic for displaying content in view mode and content entry, to not re-write again the same
        return (
          <HtmlRenderer className={styles.text} html={content.content.body} />
        );
      default:
        return (
          <div className={styles.unsupportedContent}>
            <p>Unsupported content type</p>
          </div>
        );
    }
  };

  return createPortal(
    <div className={styles.overlay}>
      <div className={classnames(styles.container)}>
        <div className={styles.header}>
          <div className={styles.headerSection}>
            <UserInitials
              name={getInitials(contents[currentSlideIndex]?.user?.name)}
            />
            <span className="text__body__semi__bold__medium__white">
              {contents[currentSlideIndex]?.user?.name || 'Unknown User'}
            </span>
          </div>
          <div className={styles.headerSection}>
            <Button buttonStyle={ButtonStyle.SecondaryGray} onClick={viewVault}>
              <div className={styles.playButton}>
                <PlayIcon />
                View
              </div>
            </Button>
            <button
              onClick={onClose}
              type="button"
              className={styles.crossButton}
            >
              <CrossIcon />
            </button>
          </div>
        </div>
        <div className={styles.content}>
          <div
            id={swiperPrevId}
            className={classnames(
              styles.customNavButton,
              styles.customNavButtonPrev,
            )}
          >
            <ArrowUpRight />
          </div>
          <div
            id={swiperNextId}
            className={classnames(
              styles.customNavButton,
              styles.customNavButtonNext,
            )}
          >
            <ArrowUpRight />
          </div>
          <Swiper
            modules={[Navigation, Thumbs]}
            spaceBetween={0}
            slidesPerView={1}
            navigation={{
              prevEl: `#${swiperPrevId}`,
              nextEl: `#${swiperNextId}`,
            }}
            thumbs={{ swiper: thumbsSwiper }}
            onSlideChange={(swiper) => setCurrentSlideIndex(swiper.activeIndex)}
            className={styles.mainSwiper}
            initialSlide={initialIndex}
          >
            {contents.map((content) => (
              <SwiperSlide key={content.id} className={styles.slide}>
                {renderContent(content)}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className={styles.footer}>
          <Swiper
            modules={[Thumbs]}
            onSwiper={setThumbsSwiper}
            spaceBetween={8}
            slidesPerView="auto"
            freeMode
            watchSlidesProgress
            className={styles.thumbsSwiper}
          >
            {contents.map((content) => (
              <SwiperSlide
                key={`thumb-${content.id}`}
                className={styles.thumbSlide}
              >
                {content.thumbnailUrl ? (
                  <>
                    <img
                      src={content.thumbnailUrl}
                      alt={content.name}
                      className={styles.thumbnail}
                    />
                    <div className={styles.thumbOverlay}>
                      {adaptContentType(content) === AlbumContentType.video && (
                        <PlayIcon fill="white" />
                      )}
                    </div>
                  </>
                ) : (
                  <div className={styles.icon}>
                    {ContentIcons[adaptContentType(content)]}
                  </div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>,
    document.body,
  );
};
